import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { restaurantConfig } from "../config/restaurantConfig";
import Reveal from "../components/Reveal";
import SectionEyebrow from "../components/SectionEyebrow";

// Compose a mixed gallery from the config buckets, preserving the brief mapping.
function composeGallery(images, cats) {
  const items = [];
  images.atmosphere.forEach((src, i) =>
    items.push({ src, label: cats.atmosphere.labels[i] ?? cats.atmosphere.labels.at(-1), category: cats.atmosphere.category })
  );
  images.gourmet.forEach((src) =>
    items.push({ src, label: cats.gourmet.label, category: cats.gourmet.category })
  );
  images.chalkboard.forEach((src) =>
    items.push({ src, label: cats.chalkboard.label, category: cats.chalkboard.category })
  );
  images.extras.forEach((src) =>
    items.push({ src, label: cats.extras.label, category: cats.extras.category })
  );
  return items;
}

export default function Gallery() {
  const g = restaurantConfig.ui.gallery;
  const tiles = composeGallery(restaurantConfig.images, g.categories);
  const [lightbox, setLightbox] = useState(null);

  return (
    <div data-testid="page-gallery" className="pb-24">
      {/* Header */}
      <section className="pt-12 pb-12 md:pt-20 md:pb-16 text-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <SectionEyebrow className="justify-center inline-flex">{g.eyebrow}</SectionEyebrow>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl mt-6 italic whitespace-pre-line" style={{ fontWeight: 400 }}>
              {g.title}
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-muted-foreground font-sans font-light">
              {g.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Masonry */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
          {tiles.map((tile, i) => (
            <Reveal key={tile.src + i} delay={(i % 6) * 0.08} className="mb-4 md:mb-6 break-inside-avoid">
              <motion.button
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`gallery-tile-${i}`}
                onClick={() => setLightbox(tile)}
                className="group relative block w-full overflow-hidden bg-card text-left will-change-transform"
              >
                <img
                  src={tile.src}
                  alt={tile.label}
                  loading="lazy"
                  className="w-full h-auto object-cover img-cinematic transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-5 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="text-[10px] tracking-luxe uppercase text-gold">{tile.category}</span>
                  <p className="font-serif italic text-zinc-50 text-xl mt-1" style={{ fontWeight: 400 }}>
                    {tile.label}
                  </p>
                </div>
              </motion.button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            data-testid="gallery-lightbox"
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label={g.closeAria}
              className="absolute top-6 right-6 w-11 h-11 inline-flex items-center justify-center rounded-full border border-[#FDFBF7]/40 text-[#FDFBF7] hover:border-gold hover:text-gold transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <motion.img
              key={lightbox.src}
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              src={lightbox.src}
              alt={lightbox.label}
              className="max-h-[85vh] max-w-[95vw] object-contain"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
